import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import baseUrl from "../api/utils";
import "../assets/css/invoicestyle.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import LinkPaymentOutModal from "./Linkpaymentoutmodal";

import { getPermission } from "../utils/permissionUtils";
import MODULES from "../utils/modules";

import AddPaymentOutModal from "./Addpaymentoutmodal";

const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2
}) : "0.00";

const PaymentOutPage = () => {
  const token = sessionStorage.getItem("token");
  const MySwal = withReactContent(Swal);
  // NOTE: assumes MODULES.PAYMENT_OUT exists in your permissions constants file,
  // same pattern as MODULES.PAYMENT_IN — add it there if it isn't already.
  const canCreate = getPermission(MODULES.PAYMENT_OUT, "canCreate");
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [filterUser, setFilterUser] = useState("All Users");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [filterMonth, setFilterMonth] = useState("All Payment-Out");
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dateTo, setDateTo] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const [showCalendar, setShowCalendar] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const monthRef = useRef(null);
  const calendarRef = useRef(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyPayment, setApplyPayment] = useState(null);

  const monthOptions = ["All Payment-Out", "This Month", "Last Month", "This Year"];

  const [totalAmount, setTotalAmount] = useState(0);
  const [totalLinked, setTotalLinked] = useState(0);
  const [totalUnused, setTotalUnused] = useState(0);

  // ── VIEW MODAL STATE — uses the row data we already have, no extra fetch ──
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPayment, setViewPayment] = useState(null);
  const [myCompany, setMyCompany] = useState(null);

  // ── ADD MODAL ──
  const [showAddModal, setShowAddModal] = useState(false);

  // ── FETCH PAYMENT-OUTS ──────────────────────────────────
  const fetchPaymentOuts = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (filterMonth === "All Payment-Out") {
        params.append("fromDate", "2000-01-01");
        params.append("toDate", "2099-12-31");
      } else {
        params.append("fromDate", dateFrom.toISOString().split("T")[0]);
        params.append("toDate", dateTo.toISOString().split("T")[0]);
      }

      const res = await axios.get(`${baseUrl}/getPaymentOuts?${params.toString()}`, {
        headers: { Authorization: token },
      });

      const data = res.status === 200 ? res.data : [];
      data.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));

      setRows(data);
      const total  = data.reduce((sum, r) => sum + (r.totalPaid || 0), 0);
      const unused = data.reduce((sum, r) => sum + (r.unusedAmount || 0), 0);
      setTotalAmount(total);
      setTotalUnused(unused);
      setTotalLinked(total - unused);
    } catch (error) {
      console.error("Error fetching payment-outs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPaymentOuts(); }, [filterMonth, dateFrom, dateTo]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${baseUrl}/admin/getMyCompany`, {
          headers: { Authorization: token },
        });
        setMyCompany(res.data);
      } catch (error) {
        console.error("Error fetching company details:", error);
      }
    };
    fetchCompany();
  }, []);

  const filteredRows = rows.filter((r) => {
    const q = searchQuery.toLowerCase();
    return (
      r.partyName?.toLowerCase().includes(q) ||
      r.voucherNo?.toString().includes(q)
    );
  });

  const userOptions = [
    { label: "All Users", initials: "AU", bg: "#e8f0fe", color: "#1a73e8" },
    { label: "Admin",     initials: "AD", bg: "#fce8e6", color: "#d93025" },
    { label: "Cashier",   initials: "CA", bg: "#e6f4ea", color: "#137333" },
  ];
  const selected = userOptions.find((u) => u.label === filterUser);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const applyDateRange = (option) => {
    const today = new Date();
    const year  = today.getFullYear();
    const month = today.getMonth();
    if (option === "This Month") {
      setDateFrom(new Date(year, month, 1));
      setDateTo(new Date(year, month + 1, 0));
      setShowCalendar(false);
    } else if (option === "Last Month") {
      setDateFrom(new Date(year, month - 1, 1));
      setDateTo(new Date(year, month, 0));
      setShowCalendar(false);
    } else if (option === "This Year") {
      setDateFrom(new Date(year, 0, 1));
      setDateTo(new Date(year, 11, 31));
      setShowCalendar(false);
    } else if (option === "All Payment-Out") {
      setShowCalendar(true);
    }
  };

  const handleMonthSelect = (option) => {
    setFilterMonth(option);
    applyDateRange(option);
    setMonthDropdownOpen(false);
  };

  const formatDate = (date) =>
    date.toLocaleDateString("en-GB").replace(/\//g, "/");

  useEffect(() => {
    const handler = (e) => {
      if (monthRef.current && !monthRef.current.contains(e.target))
        setMonthDropdownOpen(false);
      if (calendarRef.current && !calendarRef.current.contains(e.target))
        setShowCalendar(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleView = (row) => {
    setViewPayment(row);
    setShowViewModal(true);
  };

  const handleApplyUnused = (row) => {
    setApplyPayment(row);
    setShowApplyModal(true);
  };

  const handleApplyDone = async (allocations) => {
    try {
      await axios.post(`${baseUrl}/applyUnusedAmountOut`, {
        paymentOutId: applyPayment.id,
        allocations,
      }, {
        headers: { Authorization: token },
      });
      setShowApplyModal(false);
      setApplyPayment(null);
      fetchPaymentOuts(); // refresh the list to show updated unused/status
    } catch (err) {
      console.error("Failed to apply unused amount", err);
      alert(err.response?.data?.message || "Failed to apply unused amount");
    }
  };

  const statusStyle = (status) => {
    if (status === "Used") return { background: "#e6f4ea", color: "#137333" };
    if (status === "Advance") return { background: "#e8f0fe", color: "#1a73e8" };
    return { background: "#fff3e0", color: "#e65100" }; // Partially Used
  };

  const splitsLabel = (splits) =>
    (splits && splits.length > 0) ? splits.map((s) => s.paymentType).join(", ") : "—";

  return (
    <div>
      <div className="invoice-card-wrapper" style={{ marginTop: "-115px" }}>
        <div>
          <div className="card invoice-card">
            <div className="card-body p-0">

              <div
                className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                style={{ gap: "12px", flexWrap: "wrap" }}
              >
                <div className="input-group input-group-sm" style={{ maxWidth: "300px" }}>
                  <div className="input-group-prepend">
                    <span className="input-group-text bg-white border-right-0">
                      <i className="fa-solid fa-magnifying-glass" style={{ color: "#aaa", fontSize: "13px" }}></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control border-left-0"
                    placeholder="Search Party / Ref No"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ boxShadow: "none", fontSize: "13px" }}
                  />
                </div>
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                  <button className="btn btn-sm btn-light" style={{ borderRadius: "6px", padding: "5px 9px" }}>
                    <i className="fa-solid fa-print" style={{ fontSize: "13px", color: "#555" }}></i>
                  </button>
                  <div onClick={() => navigate(-1)} style={{ cursor: "pointer", fontSize: "18px", color: "#555" }}>
                    <i className="fa-solid fa-xmark"></i>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                <h5 className="mb-0 font-weight-bold" style={{ fontSize: "16px" }}>Payment-Out</h5>
                {canCreate && (
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setShowAddModal(true)}
                      style={{ fontWeight: "600", borderRadius: "6px", padding: "5px 16px" }}
                    >
                      <i className="fa-solid fa-plus mr-1"></i> Add Payment-Out
                    </button>
                  </div>
                )}
              </div>

              <div
                className="d-flex align-items-center px-3 py-2 border-bottom"
                style={{ background: "#fafafa", gap: "10px", flexWrap: "wrap" }}
              >
                <span style={{ fontSize: "13px", color: "#555", fontWeight: "500" }}>Filter by :</span>

                <div ref={monthRef} style={{ position: "relative" }}>
                  <div
                    className="d-flex align-items-center"
                    onClick={() => setMonthDropdownOpen((p) => !p)}
                    style={{ border: "1px solid #ddd", borderRadius: "20px", padding: "3px 12px", background: "#fff", cursor: "pointer", gap: "6px", fontSize: "13px" }}
                  >
                    <span>{filterMonth}</span>
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: "10px", color: "#888", transition: "transform 0.2s", transform: monthDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}></i>
                  </div>
                  {monthDropdownOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 180, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 200, padding: "6px 0" }}>
                      {monthOptions.map((opt) => (
                        <div
                          key={opt}
                          onClick={() => handleMonthSelect(opt)}
                          style={{ padding: "8px 16px", fontSize: 13, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: filterMonth === opt ? "#f5f5f5" : "transparent" }}
                        >
                          <span>{opt}</span>
                          {filterMonth === opt && <span style={{ color: "#1a73e8", fontSize: 13 }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div ref={calendarRef} style={{ position: "relative" }}>
                  <div
                    className="d-flex align-items-center"
                    onClick={() => { if (filterMonth === "All Payment-Out") setShowCalendar((p) => !p); }}
                    style={{ border: "1px solid #ddd", borderRadius: "20px", padding: "3px 12px", background: "#fff", cursor: filterMonth === "All Payment-Out" ? "pointer" : "default", gap: "6px", fontSize: "13px" }}
                  >
                    <i className="fa-regular fa-calendar" style={{ color: "#888", fontSize: "12px" }}></i>
                    <span>{formatDate(dateFrom)}</span>
                    <span style={{ color: "#aaa" }}>To</span>
                    <span>{formatDate(dateTo)}</span>
                  </div>
                  {showCalendar && filterMonth === "All Payment-Out" && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Select date range</div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 12, color: "#555", minWidth: 30 }}>From</span>
                        <DatePicker selected={dateFrom} onChange={(date) => setDateFrom(date)} selectsStart startDate={dateFrom} endDate={dateTo} dateFormat="dd/MM/yyyy" className="form-control form-control-sm" />
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 12, color: "#555", minWidth: 30 }}>To</span>
                        <DatePicker selected={dateTo} onChange={(date) => setDateTo(date)} selectsEnd startDate={dateFrom} endDate={dateTo} minDate={dateFrom} dateFormat="dd/MM/yyyy" className="form-control form-control-sm" />
                      </div>
                    </div>
                  )}
                </div>

                {/* NOTE: this User filter is UI-only for now — /getPaymentOuts doesn't
                    accept a createdBy param yet. Wire it up backend-side (same pattern
                    as getBillsFiltered) if you want it functional. */}
                <div ref={dropdownRef} style={{ position: "relative" }}>
                  <div
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    style={{ display: "flex", alignItems: "center", gap: "6px", border: "1px solid #ddd", borderRadius: "20px", padding: "3px 12px", background: "#fff", cursor: "pointer", fontSize: "13px", userSelect: "none" }}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: selected.bg, color: selected.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 500, flexShrink: 0 }}>
                      {selected.initials}
                    </div>
                    <span>{filterUser}</span>
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: 10, color: "#888", transition: "transform 0.2s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </div>
                  {dropdownOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 180, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, padding: "6px 0" }}>
                      {userOptions.map((opt, i) => (
                        <div key={opt.label}>
                          {i === 1 && <hr style={{ margin: "4px 14px", border: "none", borderTop: "0.5px solid #eee" }} />}
                          <div
                            onClick={() => { setFilterUser(opt.label); setDropdownOpen(false); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", cursor: "pointer", fontSize: 13, background: filterUser === opt.label ? "#f5f5f5" : "transparent" }}
                          >
                            <div style={{ width: 22, height: 22, borderRadius: "50%", background: opt.bg, color: opt.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 500 }}>
                              {opt.initials}
                            </div>
                            <span>{opt.label}</span>
                            {filterUser === opt.label && <span style={{ marginLeft: "auto", color: "#1a73e8", fontSize: 13 }}>✓</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="px-3 pt-3">
                <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "14px 18px", background: "#fff", maxWidth: "300px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <span style={{ fontSize: "13px", color: "#888" }}>Total Amount Paid</span>
                  </div>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#222", marginBottom: "2px" }}>
                    ₹ {totalAmount.toLocaleString()}
                  </div>
                  <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: "8px" }}>
                    <div className="d-flex" style={{ gap: "10px", fontSize: "12px" }}>
                      <span style={{ color: "#555" }}>Linked: <strong style={{ color: "#222" }}>₹ {totalLinked.toLocaleString()}</strong></span>
                      <span style={{ color: "#ccc" }}>|</span>
                      <span style={{ color: "#555" }}>Unused: <strong style={{ color: "#222" }}>₹ {totalUnused.toLocaleString()}</strong></span>
                    </div>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-danger"></div>
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ paddingTop: "30px", paddingBottom: "50px" }}>
                  <div style={{ width: "100px", height: "100px", background: "#fdeeee", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px", boxShadow: "0 4px 16px rgba(237,47,47,0.10)" }}>
                    <i className="fa-solid fa-money-bill-transfer" style={{ fontSize: "40px", color: "#ed2f2f" }}></i>
                  </div>
                  <p style={{ fontSize: "15px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>No Payments to show</p>
                  <p style={{ fontSize: "13px", color: "#aaa", marginBottom: "18px" }}>You haven't recorded any payments yet.</p>
                  {canCreate && (
                    <button
                      className="btn btn-danger"
                      onClick={() => setShowAddModal(true)}
                      style={{ borderRadius: "8px", fontWeight: "600", padding: "8px 24px", fontSize: "14px" }}
                    >
                      <i className="fa-solid fa-plus mr-2"></i> Add Payment-Out
                    </button>
                  )}
                </div>
              ) : (
                <div className="px-3 pt-2" style={{ overflowY: "auto", maxHeight: "420px" }}>
                  <table className="table table-hover table-bordered table-sm invoice-table" style={{ fontSize: "13px" }}>
                    <thead style={{ background: "#222", color: "#fff" }}>
                      <tr>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Ref No.</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Date</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Party Name</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Payment Type</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Amount (₹)</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Unused (₹)</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Status</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r, idx) => (
                        <tr key={r.id || idx}>
                          <td>{r.voucherNo}</td>
                          <td>{r.paymentDate}</td>
                          <td>{r.partyName}</td>
                          <td>{splitsLabel(r.splits)}</td>
                          <td>₹ {fmt(r.totalPaid)}</td>
                          <td style={{ color: r.unusedAmount > 0 ? "#e65100" : "#aaa" }}>
                            ₹ {fmt(r.unusedAmount)}
                          </td>
                          <td>
                            <span style={{
                              padding: "2px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "600",
                              ...statusStyle(r.status),
                            }}>
                              {r.status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex" style={{ gap: "6px" }}>
                              <button
                                className="btn btn-sm btn-light"
                                title="View"
                                onClick={() => handleView(r)}
                              >
                                <i className="fa-solid fa-eye" style={{ color: "#2f80ed" }}></i>
                              </button>
                              {r.unusedAmount > 0 && (
                                <button
                                  className="btn btn-sm btn-light"
                                  title="Apply Unused Amount"
                                  onClick={() => handleApplyUnused(r)}
                                >
                                  <i className="fa-solid fa-link" style={{ color: "#137333" }}></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ── VIEW MODAL — uses the row's already-fetched allocations/splits, no extra call ── */}
      {showViewModal && viewPayment && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowViewModal(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "10px", width: "600px",
              maxWidth: "95%", maxHeight: "90vh", overflowY: "auto",
              padding: "24px", position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowViewModal(false)}
              style={{
                position: "absolute", top: 1, right: 2, background: "none",
                border: "none", fontSize: 18, color: "#888", cursor: "pointer",
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            <div className="d-flex justify-content-between align-items-start mb-3 pb-3" style={{ borderBottom: "1px solid #eee" }}>
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                {myCompany?.logo && (
                  <img src={`data:image/jpeg;base64,${myCompany.logo}`} alt="logo" style={{ width: 50, height: 50, objectFit: "contain" }} />
                )}
                <div>
                  <h5 className="mb-0" style={{ fontWeight: 700 }}>{myCompany?.businessName || "—"}</h5>
                  <p className="mb-0" style={{ fontSize: 12, color: "#777" }}>{myCompany?.businessAddress}</p>
                </div>
              </div>
              <div className="text-right">
                <h5 style={{ fontWeight: 700, color: "#c0392b" }}>PAYMENT VOUCHER</h5>
                <p className="mb-0" style={{ fontSize: 13 }}>#{viewPayment.voucherNo}</p>
                <p className="mb-0" style={{ fontSize: 12, color: "#777" }}>{viewPayment.paymentDate}</p>
              </div>
            </div>

            <div className="mb-3">
              <p style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>PAID TO</p>
              <p style={{ fontWeight: 600, marginBottom: 2 }}>{viewPayment.partyName}</p>
              {viewPayment.phoneNo && <p style={{ fontSize: 13, color: "#555" }}>{viewPayment.phoneNo}</p>}
            </div>

            <div className="mb-3" style={{ fontSize: 13 }}>
              Payment Mode: <strong>{splitsLabel(viewPayment.splits)}</strong>
            </div>

            <p style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>APPLIED TO</p>
            <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Bill No</th><th>Date</th><th>Total</th><th>Linked Amount</th>
                </tr>
              </thead>
              <tbody>
                {(viewPayment.allocations || []).length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: "center", color: "#aaa" }}>Not linked to any bill</td></tr>
                ) : viewPayment.allocations.map((a, idx) => (
                  <tr key={idx}>
                    <td>{a.supplierBillNumber || "—"}</td>
                    <td>{a.billDate}</td>
                    <td>₹{fmt(a.totalAmount)}</td>
                    <td>₹{fmt(a.linkedAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="d-flex justify-content-end">
              <table style={{ fontSize: 13, minWidth: 220 }}>
                <tbody>
                  <tr><td className="text-muted pr-3">Total Paid</td><td className="text-right">₹{fmt(viewPayment.totalPaid)}</td></tr>
                  <tr>
                    <td className="text-muted pr-3">Unused (Advance)</td>
                    <td className="text-right" style={{ color: viewPayment.unusedAmount > 0 ? "#e65100" : "#27ae60", fontWeight: 600 }}>
                      ₹{fmt(viewPayment.unusedAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {viewPayment.description && (
              <div className="mt-3 pt-2" style={{ borderTop: "1px solid #f0f0f0", fontSize: 13 }}>
                <p style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>NOTE</p>
                {viewPayment.description}
              </div>
            )}

            <div className="mt-3 pt-2" style={{ borderTop: "1px solid #f0f0f0", fontSize: 11, color: "#aaa" }}>
              Created by {viewPayment.createdBy}
            </div>
          </div>
        </div>
      )}

      {showApplyModal && applyPayment && (
        <LinkPaymentOutModal
          supplierId={applyPayment.supplierId}
          partyName={applyPayment.partyName}
          paidAmount={applyPayment.unusedAmount}
          initialAllocations={[]}
          onCancel={() => { setShowApplyModal(false); setApplyPayment(null); }}
          onDone={handleApplyDone}
        />
      )}


      {showAddModal && (
        <AddPaymentOutModal
          onClose={() => setShowAddModal(false)}
          onSaved={fetchPaymentOuts}
        />
      )}
    </div>
  );
};

export default PaymentOutPage;